import React from 'react';
import { HealthScoreProps } from '../../../types/health-tools/predictive-types';
import HealthCard from '../ui/HealthCard';
import { BarChart3, Sparkles } from 'lucide-react';

/**
 * Health Score Component
 * 
 * Displays a user's overall health score with visual indicators
 * 
 * @param score - The health score (0-100)
 * @param className - Additional CSS classes
 */
const HealthScore: React.FC<HealthScoreProps> = ({
  score,
  className = '',
}) => {
  // Ensure score is within bounds
  const safeScore = Math.min(Math.max(score, 0), 100);
  
  // Determine score category
  const getScoreCategory = () => {
    if (safeScore < 40) return 'Poor';
    if (safeScore < 60) return 'Fair';
    if (safeScore < 80) return 'Good';
    return 'Excellent';
  };
  
  return (
    <HealthCard 
      title="Health Score" 
      icon={BarChart3} 
      className={className}
    >
      <div className="flex justify-center my-4">
        <div className="relative">
          <div className="text-center mb-2">
            <span className="text-5xl font-bold text-[var(--text-primary)] flex items-center justify-center">
              {safeScore}
              <Sparkles className="w-5 h-5 text-yellow-500 ml-2 animate-pulse" />
            </span>
            <p className="text-sm text-[var(--text-secondary)] mt-1">
              {getScoreCategory()}
            </p>
          </div>
          
          <div className="w-full mt-6">
            <div className="flex justify-between text-xs text-[var(--text-tertiary)] mb-1">
              <span>Poor</span>
              <span>Good</span>
              <span>Excellent</span>
            </div>
            <div className="w-full h-2 bg-[var(--background-inset)] rounded-full">
              <div 
                className="h-full rounded-full bg-gradient-to-r from-[var(--status-error)] via-[var(--status-warning)] to-[var(--status-success)]" 
                style={{ width: `${safeScore}%` }}
              ></div>
            </div>
            <div className="h-4 relative">
              <div 
                className="absolute h-4 w-4 bg-[var(--brand-primary)] rounded-full -top-3" 
                style={{ left: `${safeScore}%` }}
              ></div>
            </div>
          </div>
        </div>
      </div>
      
      <p className="text-sm text-[var(--text-secondary)] text-center mt-4">
        Your health score is calculated based on biometric data, lifestyle choices, and health history.
      </p>
    </HealthCard>
  );
};

export default HealthScore; 